import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";

import { creditsService } from "../services/creditsService";
import { ACTING_AS_KEY } from "../services/api";
import { light as C, MONO, radius } from "../../hiring/theme";

// Kumudu working inside a customer's hiring module.
//
// WHY THIS IS A BAR AND NOT A QUIET DROPDOWN
// The dangerous state is not switching store — it is FORGETTING you switched.
// Somebody who opens a customer's pipeline to debug a failed interview, wanders
// off, comes back and starts rejecting people is a real thing that happens, and
// the fix is that the screen refuses to look normal while it is showing
// somebody else's data.
//
// So while acting as another store this is a full-width amber bar that cannot
// be dismissed, naming the store. When Kumudu is in its own store it collapses
// to a single quiet button.
//
// SELF-HIDING
// Nothing here needs to know who the user is. It asks the API for the store
// list; a customer tenant gets a 404 from that route and this renders nothing.
// The permission lives on the server and the UI simply reflects it, rather than
// two places both deciding who is the platform.

const read = () => {
  try {
    return localStorage.getItem(ACTING_AS_KEY) || "";
  } catch {
    return "";
  }
};

const write = (v) => {
  try {
    if (v) localStorage.setItem(ACTING_AS_KEY, String(v));
    else localStorage.removeItem(ACTING_AS_KEY);
  } catch {
    /* private mode: the switch just will not persist */
  }
};

export default function ActingAsBar() {
  const [stores, setStores] = useState(null); // null = unknown, [] = not platform
  const [actingAs, setActingAs] = useState(read());
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await creditsService.listStores();
      setStores(res?.data?.data?.stores || []);
    } catch {
      // 404 for a customer tenant, and anything else is equally a reason not to
      // show a control that would not work.
      setStores([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A full page load is deliberate rather than a state refresh. Every screen in
  // the module has already fetched data for the previous store — the pipeline,
  // the usage dashboard, whatever is open in a drawer — and re-fetching only
  // what this component knows about would leave the rest showing the wrong
  // company's candidates.
  const switchTo = (storeId) => {
    write(storeId);
    window.location.reload();
  };

  // ── The way out must never depend on a network call ───────────────────────
  //
  // This used to be `if (!stores || stores.length === 0) return null`, which is
  // right for a customer tenant but also fires while the first fetch is still
  // in flight, when that fetch fails, and when the store being worked in stops
  // returning data — a settings row that goes missing 403s EVERY hiring route,
  // this one included.
  //
  // In each of those cases somebody already inside a customer's store lost the
  // only control that gets them out, while the header kept going out on every
  // request. The remedy was clearing localStorage by hand, which is not a
  // remedy.
  //
  // So once we are acting as somebody, the bar renders unconditionally. What a
  // failed lookup costs us is the store's NAME, not the knowledge that we are
  // standing in it.
  const loading = stores === null;
  if (!actingAs && (loading || stores.length === 0)) return null;

  const list = stores || [];
  const platform = list.find((s) => s.is_platform);
  const current = list.find((s) => String(s.store_id) === String(actingAs));
  const others = list.filter((s) => !s.is_platform);

  const filtered = search.trim()
    ? others.filter(
        (s) =>
          s.name.toLowerCase().includes(search.trim().toLowerCase()) ||
          String(s.store_id) === search.trim(),
      )
    : others;

  return (
    <>
      {actingAs ? (
        // ── Impossible to miss, impossible to dismiss ──────────────────────
        <Box
          sx={{
            bgcolor: "#b45309",
            color: "#fff",
            px: { xs: 1.75, md: 3 },
            py: 1,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ maxWidth: 1280, mx: "auto" }}
          >
            <StorefrontRoundedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 650, minWidth: 0 }} noWrap>
              You are working inside{" "}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {current?.name || `store ${actingAs}`}
              </Box>
              <Box
                component="span"
                sx={{ fontWeight: 500, opacity: 0.85, display: { xs: "none", sm: "inline" } }}
              >
                {" "}— everything on screen is their data, not Kumudu's.
              </Box>
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              onClick={() => setPicking(true)}
              sx={{
                textTransform: "none",
                fontWeight: 650,
                fontSize: 12.5,
                color: "#fff",
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              Change
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => switchTo("")}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                color: "#fff",
                borderColor: "rgba(255,255,255,.6)",
                flexShrink: 0,
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,.12)" },
              }}
            >
              Back to {platform?.name || "Kumudu"}
            </Button>
          </Stack>
        </Box>
      ) : (
        // Quiet when it does not matter. Kumudu's own store is the normal state
        // and should not carry a warning.
        <Box sx={{ px: { xs: 1.75, md: 3 }, pt: 1 }}>
          <Box sx={{ maxWidth: 1280, mx: "auto" }}>
            <Button
              size="small"
              startIcon={<SwapHorizRoundedIcon sx={{ fontSize: 17 }} />}
              onClick={() => setPicking(true)}
              sx={{
                textTransform: "none",
                fontWeight: 650,
                fontSize: 12.5,
                color: C.muted,
                "&:hover": { color: C.act, bgcolor: C.actSoft },
              }}
            >
              Open another store
            </Button>
          </Box>
        </Box>
      )}

      <Dialog
        open={picking}
        onClose={() => setPicking(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: radius.lg } }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 11,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: C.faint,
                }}
              >
                Platform
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: 19, fontWeight: 700, color: C.ink }}>
                Open a store's hiring
              </Typography>
              <Typography sx={{ fontSize: 13, color: C.muted, mt: 0.25, lineHeight: 1.55 }}>
                You will see their jobs, candidates and interviews. Anything you
                do is recorded against your own account.
              </Typography>
            </Box>
            <Button onClick={() => setPicking(false)} sx={{ minWidth: 0, p: 0.5, color: C.faint }}>
              <CloseRoundedIcon sx={{ fontSize: 19 }} />
            </Button>
          </Stack>

          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search by name or store id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchRoundedIcon sx={{ fontSize: 18, color: C.faint, mr: 1 }} />,
            }}
          />

          <Box sx={{ mt: 1.5, maxHeight: 320, overflowY: "auto" }}>
            {actingAs && (
              <Box
                onClick={() => switchTo("")}
                sx={{
                  py: 1.25,
                  px: 1,
                  borderRadius: radius.sm,
                  cursor: "pointer",
                  mb: 0.5,
                  bgcolor: C.actSoft,
                  "&:hover": { bgcolor: C.actSoft },
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.act }}>
                  ← Back to {platform?.name || "Kumudu"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: C.muted }}>Your own hiring</Typography>
              </Box>
            )}

            {filtered.length === 0 && (
              <Typography sx={{ py: 4, textAlign: "center", fontSize: 13.5, color: C.muted }}>
                {others.length === 0
                  ? "No customer store has hiring yet. Grant one credits to switch it on."
                  : "No store matches that."}
              </Typography>
            )}

            {filtered.map((s) => {
              const active = String(s.store_id) === String(actingAs);
              return (
                <Box
                  key={s.store_id}
                  onClick={() => !active && switchTo(s.store_id)}
                  sx={{
                    py: 1.25,
                    px: 1,
                    borderRadius: radius.sm,
                    cursor: active ? "default" : "pointer",
                    borderBottom: `1px solid ${C.hair}`,
                    bgcolor: active ? C.panel : "transparent",
                    "&:hover": { bgcolor: active ? C.panel : C.actSoft },
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 650, color: C.ink }} noWrap>
                    {s.name}
                    {active && (
                      <Box component="span" sx={{ ml: 1, fontSize: 11.5, color: C.muted }}>
                        (open now)
                      </Box>
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: C.faint }}>
                    store {s.store_id}
                    {s.exempt ? "" : ` · ${s.balance} credits`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
